// invest_FE/src/pages/Admin/AnnouncementPage.js
import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../../context/UserContext';
import { NotificationContext } from '../../context/NotificationContext';
import { getAllAnnouncements, setActiveAnnouncement, deleteAnnouncement, createAnnouncement, updateAnnouncement, deactivateCurrentAnnouncement } from '../../services/announcementService';
import './AnnouncementPage.css';

const AnnouncementPage = () => {
  const { user } = useContext(UserContext);
  const { addNotification } = useContext(NotificationContext);

  const [announcements, setAnnouncements] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for the modal form
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentAnnouncement, setCurrentAnnouncement] = useState(null); // null for new, object for edit
  const [isSaving, setIsSaving] = useState(false);

  const fetchAnnouncements = async () => {
    if (!user.token) return;
    try {
      setIsLoading(true);
      const data = await getAllAnnouncements(user.token);
      setAnnouncements(data);
      setError(null);
    } catch (err) {
      setError('Failed to load announcements. Please try again.');
      addNotification(err.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.token]);

  const handleSetActive = async (id) => {
    try {
      await setActiveAnnouncement(id, user.token);
      addNotification('Announcement set as active.', 'success');
      // Refetch to update the status for all items
      fetchAnnouncements();
    } catch (err) {
      addNotification(`Error setting active announcement: ${err.message}`, 'error');
    }
  };

  const handleDeactivate = async () => {
    try {
      await deactivateCurrentAnnouncement(user.token);
      addNotification('Announcement deactivated.', 'success');
      fetchAnnouncements(); // Refetch to update status
    } catch (err) {
      addNotification(`Error deactivating announcement: ${err.message}`, 'error');
    }
  };

  const handleDelete = (id) => {
    addNotification('Are you sure you want to delete this announcement?', 'warning', 0, true, [
      { text: 'Cancel', onClick: () => {} },
      {
        text: 'Yes, Delete',
        onClick: async () => {
          try {
            await deleteAnnouncement(id, user.token);
            addNotification('Announcement deleted.', 'success');
            setAnnouncements((prev) => prev.filter((a) => a._id !== id));
          } catch (err) {
            addNotification(`Error deleting announcement: ${err.message}`, 'error');
          }
        },
        className: 'delete-btn',
      },
    ]);
  };

  const handleOpenCreateModal = () => {
    setCurrentAnnouncement(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (announcement) => {
    setCurrentAnnouncement(announcement);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentAnnouncement(null);
  };

  const handleSave = async (formData) => {
    setIsSaving(true);
    try {
      if (currentAnnouncement) {
        // Update existing
        await updateAnnouncement(currentAnnouncement._id, formData, user.token);
        addNotification('Announcement updated successfully!', 'success');
      } else {
        // Create new
        await createAnnouncement(formData, user.token);
        addNotification('Announcement created successfully!', 'success');
      }
      handleCloseModal();
      fetchAnnouncements(); // Refresh the list
    } catch (err) {
      addNotification(`Failed to save announcement: ${err.message}`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className="announcement-page-container">Loading...</div>;
  }

  if (error) {
    return <div className="announcement-page-container error-message">{error}</div>;
  }

  return (
    <div className="announcement-page-container">
      <div className="page-header">
        <h2>Manage Announcements</h2>
        <button onClick={handleOpenCreateModal} className="create-new-btn">
          + New Announcement
        </button>
      </div>

      <div className="announcements-list">
        {announcements.length === 0 ? (
          <p>No announcements found. Create one to get started.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th className="color-column-header"></th>
                <th>Title</th>
                <th>Type</th>
                <th>Status</th>
                <th>Countdown</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {announcements.map((ann) => (
                <tr key={ann._id} className={ann.isActive ? 'active-row' : ''}>
                  <td>
                    <div className="color-indicator" style={{ backgroundColor: ann.backgroundColor }}></div>
                  </td>
                  <td>{ann.title}</td>
                  <td>
                    <span className={`type-badge type-${ann.type || 'standard'}`}>{ann.type || 'standard'}</span>
                  </td>
                  <td>
                    <span className={`status-badge ${ann.isActive ? 'status-active' : 'status-inactive'}`}>{ann.isActive ? 'Active' : 'Inactive'}</span>
                  </td>
                  <td>{ann.isActive && ann.countdownTarget ? `Ends: ${new Date(ann.countdownTarget).toLocaleString()}` : ann.countdownDurationMinutes ? `${ann.countdownDurationMinutes} min` : 'None'}</td>
                  <td className="actions-cell">
                    {ann.isActive ? (
                      <button onClick={handleDeactivate} className="action-btn deactivate-btn">
                        Deactivate
                      </button>
                    ) : (
                      <button onClick={() => handleSetActive(ann._id)} className="action-btn activate-btn">
                        Set Active
                      </button>
                    )}
                    <button onClick={() => handleOpenEditModal(ann)} className="action-btn edit-btn">
                      Edit
                    </button>
                    <button onClick={() => handleDelete(ann._id)} className="action-btn delete-btn">
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {isModalOpen && <AnnouncementForm announcement={currentAnnouncement} onSave={handleSave} onClose={handleCloseModal} isSaving={isSaving} />}
    </div>
  );
};

// --- Form Component (kept in the same file for simplicity) ---
const AnnouncementForm = ({ announcement, onSave, onClose, isSaving }) => {
  const [title, setTitle] = useState(announcement?.title || '');
  const [message, setMessage] = useState(announcement?.message || '');
  const [countdownDurationMinutes, setCountdownDurationMinutes] = useState(announcement?.countdownDurationMinutes || '');
  const [backgroundColor, setBackgroundColor] = useState(announcement?.backgroundColor || '#FFC107');
  const [type, setType] = useState(announcement?.type || 'standard');

  const { addNotification } = useContext(NotificationContext);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim() || !message.trim()) {
      addNotification('Title and message are required.', 'warning');
      return;
    }
    onSave({
      title,
      message,
      countdownDurationMinutes: countdownDurationMinutes || null,
      backgroundColor,
      type,
    });
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <h3>{announcement ? 'Edit Announcement' : 'Create New Announcement'}</h3>

          <div className="form-group">
            <label htmlFor="announcement-title">Title</label>
            <input type="text" id="announcement-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength="100" placeholder="e.g., Maintenance Alert" required disabled={isSaving} />
          </div>

          <div className="form-group">
            <label htmlFor="announcement-message">Message</label>
            <textarea
              id="announcement-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows="4"
              maxLength="500"
              placeholder="Use [countdown] and/or [datetime]. e.g., Maintenance at [datetime] (in [countdown])."
              required
              disabled={isSaving}
            />
            <small className="char-counter">{500 - message.length} characters remaining</small>
          </div>

          <div className="form-group">
            <label htmlFor="announcement-type">Type</label>
            <select id="announcement-type" value={type} onChange={(e) => setType(e.target.value)} disabled={isSaving}>
              <option value="standard">Standard</option>
              <option value="shutdown">Shutdown</option>
            </select>
            <small className="description">A 'Shutdown' announcement will trigger a graceful system shutdown when activated.</small>
          </div>

          <div className="form-group">
            <label htmlFor="countdown-duration">Optional Countdown Duration (in minutes)</label>
            <input type="number" id="countdown-duration" value={countdownDurationMinutes} onChange={(e) => setCountdownDurationMinutes(e.target.value)} min="0" disabled={isSaving} />
            <small className="description">Set a duration in minutes. The countdown will start when the announcement is activated.</small>
          </div>

          <div className="form-group">
            <label>Background Color</label>
            <div className="color-picker">
              {['#FFC107', '#28A745', '#FD7E14', '#DC3545'].map((color) => (
                <div key={color} className={`color-swatch ${backgroundColor === color ? 'selected' : ''}`} style={{ backgroundColor: color }} onClick={() => !isSaving && setBackgroundColor(color)} title={color} />
              ))}
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="cancel-btn" onClick={onClose} disabled={isSaving}>
              Cancel
            </button>
            <button type="submit" className="save-button" disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AnnouncementPage;
